import { css } from 'aphrodite';
import moment from 'moment';
import * as React from 'react';
import { useState } from 'react';
import { CoffeeLeadIssueCategory, IRange, IUser } from '../../../../../extViewmodels';
import { ExportIcon } from '../../../../../web/components/svgs/icons/ExportIcon';
import { UserAutocomplete } from '../../../../../web/components/UserAutocomplete';
import { Button } from '../../../../../web/components-v2/Button';
import { DateRangePicker } from '../../../../../web/components-v2/DateRangePicker';
import { Label } from '../../../../../web/components-v2/Label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../../../web/components-v2/Select';
import { CompletionStatus, IIssuesLogFilter } from '../index';
import { styleSheet } from '../styles';

const StatusOptions = [
	{ label: 'All', value: '' },
	{ label: 'Not Completed', value: 'incomplete' },
	{ label: 'Completed', value: 'complete' },
];

const CategoryOptions = [
	{ label: 'All Categories', value: '' },
	{ label: 'Wrong Location/Timezone', value: CoffeeLeadIssueCategory.WrongLocationOrTimeZone },
	{ label: 'Duplicate', value: CoffeeLeadIssueCategory.Duplicate },
	{ label: 'Current Client Needs Attention', value: CoffeeLeadIssueCategory.CurrentClientNeedsAttention },
	{ label: 'Other', value: CoffeeLeadIssueCategory.Other },
];

const DATE_FORMAT = 'YYYY-MM-DD';

interface IDateRangeValue {
	from: string;
	to: string;
}

interface IProps {
	exportingCsv?: boolean;
	onChange?: (filter: IIssuesLogFilter) => void;
	onExport?: () => void;
}

export const ListHeader: React.FC<IProps> = ({ exportingCsv, onChange, onExport }) => {
	const [currentFilter, setCurrentFilter] = useState<IIssuesLogFilter>({});
	const [dateRange, setDateRange] = useState<IDateRangeValue>({ from: '', to: '' });

	const handleUserChange = (user: IUser | null) => {
		const next = { ...currentFilter, user: user || undefined };
		setCurrentFilter(next);
		onChange?.(next);
	};

	// The picker speaks YYYY-MM-DD strings; the filter reads UTC dates (see issuesLogFilterToCoffeeLeadIssueFilter).
	const handleDateRangeChange = (from: string, to: string) => {
		setDateRange({ from, to });
		const date: IRange<Date> | undefined =
			from && to
				? { start: moment.utc(from, DATE_FORMAT).toDate(), end: moment.utc(to, DATE_FORMAT).toDate() }
				: undefined;
		const next = { ...currentFilter, date };
		setCurrentFilter(next);
		onChange?.(next);
	};

	const handleStatusChange = (value: string) => {
		const completionStatus = (value || undefined) as CompletionStatus;
		const next = { ...currentFilter, completionStatus };
		setCurrentFilter(next);
		onChange?.(next);
	};

	const handleCategoryChange = (value: string) => {
		const category = value || undefined;
		const next = { ...currentFilter, category: category as CoffeeLeadIssueCategory };
		setCurrentFilter(next);
		onChange?.(next);
	};

	return (
		<div className={css(styleSheet.filter)}>
			<div className={css(styleSheet.filterGroup, styleSheet.filterGroupUser)}>
				<Label htmlFor='issues-log-user-combobox' styles={[styleSheet.filterLabel]}>
					Reporter
				</Label>
				<UserAutocomplete
					ariaLabel='Reporter'
					id='issues-log-user-combobox'
					onChange={handleUserChange}
					placeholder='Filter by reporter'
					value={currentFilter.user ?? null}
				/>
			</div>

			<div className={css(styleSheet.filterGroup, styleSheet.filterGroupDate)}>
				<span className={css(styleSheet.filterLabel)}>Date Range</span>
				<DateRangePicker
					ariaLabel='Filter by creation date'
					from={dateRange.from}
					to={dateRange.to}
					onChange={handleDateRangeChange}
					size='sm'
				/>
			</div>

			<div className={css(styleSheet.filterGroup, styleSheet.filterGroupCategory)}>
				<Label htmlFor='issue-category-select' styles={[styleSheet.filterLabel]}>
					Category
				</Label>
				<Select value={currentFilter.category || ''} onValueChange={handleCategoryChange} items={CategoryOptions}>
					<SelectTrigger id='issue-category-select' styles={styleSheet.triggerFill}>
						<SelectValue placeholder='All Categories' />
					</SelectTrigger>
					<SelectContent>
						{CategoryOptions.map(option => (
							<SelectItem key={option.value} value={option.value}>
								{option.label}
							</SelectItem>
						))}
					</SelectContent>
				</Select>
			</div>

			<div className={css(styleSheet.filterGroup, styleSheet.filterGroupStatus)}>
				<Label htmlFor='issue-status-select' styles={[styleSheet.filterLabel]}>
					Status
				</Label>
				<Select value={currentFilter.completionStatus || ''} onValueChange={handleStatusChange} items={StatusOptions}>
					<SelectTrigger id='issue-status-select' styles={styleSheet.triggerFill}>
						<SelectValue placeholder='All' />
					</SelectTrigger>
					<SelectContent>
						{StatusOptions.map(option => (
							<SelectItem key={option.value} value={option.value}>
								{option.label}
							</SelectItem>
						))}
					</SelectContent>
				</Select>
			</div>

			<div className={css(styleSheet.fill)} />
			<Button variant='ghost' styles={[styleSheet.exportButton]} onClick={onExport} isLoading={exportingCsv}>
				<ExportIcon fillColor='currentColor' />
				Export
			</Button>
		</div>
	);
};
